import { all, call, put, takeLatest } from 'redux-saga/effects';
import * as listActions from '@/list/list-actions';
import { getFilteredInspections } from '@/property-inspections/queries';
import { getGraphqlQueryData, getPaginatedData } from '@/utils/paginated-fetch';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import {
  LICENSES_URL_PATH,
  LISTINGS_URL_BASE_PATH,
  WAITLISTED_LICENSES_URL_PATH,
  configTypes,
  listDataKeys,
} from './list-constants';
import {
  isCustomFilteredLicenseRegType,
  isLicenseConfigRegType,
} from './list-utils';
import { INSPECTION_BOOKED } from '@/property-inspections/constants';

function regTypeDataKeyFactory(regType) {
  if (isLicenseConfigRegType(regType)) {
    return listDataKeys.LICENSES;
  }
  return listDataKeys.LISTINGS;
}

/**
 * Recently we were told that the regType strlicense does not belong in the same endpoint as
 * /listings. This function will return the appropriate url path based on the regType
 */
function regTypeUrlFactory(regType) {
  if (
    regType === configTypes.STR_LICENSE ||
    isCustomFilteredLicenseRegType(regType)
  ) {
    return LICENSES_URL_PATH;
  }

  if (regType === configTypes.WAITLIST) {
    return WAITLISTED_LICENSES_URL_PATH;
  }

  return `${LISTINGS_URL_BASE_PATH}/${regType}`;
}

function* getListingDataSaga(listingConfig) {
  const listingType = listingConfig.regType || listingConfig;
  const { parameters } = listingConfig;
  // TODO: server is throwing an error when the data does not exist, should return []...
  try {
    yield put(listActions.setListingDataInFlight({ listingType }));
    const urlPath = regTypeUrlFactory(listingType);
    /*
     *  I'm fetching all list data first before pushing them into the store for two reasons:
     *  1) The list container component keeps initializing every time the listing data is
     *   concatenated.
     *  2) Doing this, does not require any major changes to the ListViewerContainer component,
     *   and preserves the inFlight logic's simplicity
     */
    let fullListData = [];
    if (listingType === 'inspections') {
      yield getGraphqlQueryData({
        variables: { regionId: 'ca-riverside-city_of_cathedral_city' },
        execFunction: getFilteredInspections,
        // eslint-disable-next-line require-yield
        *serverReturnedSaga(elements) {
          fullListData =
            elements.data.getFilteredInspections.allInspections.map(
              (inspection) => ({
                ...inspection,
                inspector: inspection.inspector || INSPECTION_BOOKED,
              })
            );
        },
      });
    } else {
      yield getPaginatedData({
        path: urlPath,
        queryParams: parameters,
        sender: strOracleApiSender,
        getItemsFromResponseData: (responseData) => {
          const dataKey = regTypeDataKeyFactory(listingType);
          return responseData[dataKey];
        },
        // eslint-disable-next-line require-yield
        *serverReturnedSaga({ pageItems }) {
          fullListData = fullListData.concat(pageItems);
        },
      });
    }

    yield put(
      listActions.serverReturnedListingData({
        listingType,
        listingData: fullListData,
      })
    );
  } catch (err) {
    console.error('Caught Error in `getListingDataSaga`', err); // eslint-disable-line no-console
    // TODO: Must go to error page
    yield put(
      listActions.serverReturnedListingData({ listingType, listingData: [] })
    );
  }
}

function* getAllListingDataSaga(action) {
  const { regTypes: availableRegTypes } = action.payload;

  const requestCalls = availableRegTypes.map((listingConfig) =>
    call(getListingDataSaga, listingConfig)
  );
  yield all(requestCalls);
}

export function* fetchAllListDataWatcher() {
  yield takeLatest(listActions.GET_ALL_LISTING_DATA, getAllListingDataSaga);
}
